import {Button, InputField, Textarea} from '@nlabs/gothamjs/components';
import {useState} from 'react';

import {fieldLabel, primaryButton, secondaryButton, textArea, textInput} from '../../theme/classes.js';

import type {Element, Workspace} from '@reactorbox/shared';
import type {FormEvent, ReactElement} from 'react';
import type {WorkspaceActions} from '../../state/workspaceActions.js';

interface ElementsPanelProps {
  readonly actions: WorkspaceActions;
  readonly busy: boolean;
  readonly workspace: Workspace;
}

export const ElementsPanel = ({actions, busy, workspace}: ElementsPanelProps): ReactElement => {
  const [editing, setEditing] = useState('');
  const [name, setName] = useState('');
  const [kind, setKind] = useState<Element['kind']>('product');
  const [description, setDescription] = useState('');
  const [referenceUrl, setReferenceUrl] = useState('');
  const edit = (element?: Element): void => {
    setEditing(element?.id || '');
    setName(element?.name || '');
    setKind(element?.kind || 'product');
    setDescription(element?.description || '');
    setReferenceUrl(element?.referenceUrl || '');
  };
  const save = (event: FormEvent): void => {
    event.preventDefault();
    void actions.mutate(editing ? `/api/elements/${editing}` : '/api/elements', {description, kind, name, referenceUrl}, 'Element saved to your reusable library.', editing ? 'PATCH' : 'POST');
  };
  return <details className="mb-5 rounded-2xl border border-white/10 bg-white/3 p-5">
    <summary className="cursor-pointer text-violet-200">Elements · Reusable models & products</summary>
    <p className="mt-3 text-sm text-white/60">Save an identity once, then select it in any shot across your projects. Add placement direction in the shot editor.</p>
    <div className="mt-5 grid gap-5 lg:grid-cols-2">
      <div className="space-y-3">{(workspace.elements || []).map((element) => <article className="rounded-xl border border-white/10 p-4" key={element.id}>
        <p className="text-xs text-sky-200">{element.kind}</p><h3 className="mt-1 text-lg">{element.name}</h3><p className="mt-2 text-sm text-white/60">{element.description}</p>
        {element.referenceUrl && <a className="mt-2 inline-block text-xs text-sky-200" href={element.referenceUrl} rel="noreferrer" target="_blank">View reference</a>}
        <Button className={`${secondaryButton} mt-3`} onClick={() => edit(element)} variant="outline">Edit element</Button>
      </article>)}{!workspace.elements?.length && <p className="text-sm text-white/50">Your saved models and products will appear here.</p>}</div>
      <form className="space-y-4" onSubmit={save}>
        <h3 className="text-lg">{editing ? 'Edit element' : 'Create an element'}</h3>
        <label className={fieldLabel}>Element name<InputField className={`${textInput} mt-2`} maxLength={160} onChange={(event) => setName(event.target.value)} required value={name} /></label>
        <label className={fieldLabel}>Element type<select className={`${textInput} mt-2`} onChange={(event) => setKind(event.target.value as Element['kind'])} value={kind}><option value="product">Product</option><option value="model">Virtual model</option></select></label>
        <label className={fieldLabel}>Defining details<Textarea className={`${textArea} mt-2`} maxLength={4000} onChange={(event) => setDescription(event.target.value)} required rows={3} value={description} /></label>
        <label className={fieldLabel}>Reference image URL<InputField className={`${textInput} mt-2`} onChange={(event) => setReferenceUrl(event.target.value)} type="url" value={referenceUrl} /></label>
        <div className="flex gap-3"><Button className={primaryButton} disabled={busy || !name.trim() || !description.trim()} type="submit">Save element</Button>{editing && <Button className={secondaryButton} onClick={() => edit()} variant="outline">New element</Button>}</div>
      </form>
    </div>
  </details>;
};
